import React from "react";

/**
 * SVG top-view of a computed mezzanine layout.
 * Consumes `result` from the /api/mezzanine/calculate response (must contain
 * `geometry` with pillars, main/secondary beams, railings, staircases, and
 * bay-width arrays).
 *
 * Shared between the configurator preview and the printed quotation.
 */

const COL_BEAM = "#FF6B1A"; // safety orange
const COL_SEC = "#FFC093"; // light orange
const COL_PILLAR = "#1E40AF"; // blue 800
const COL_RAIL = "#2563EB"; // blue 600
const COL_DIM = "#475569"; // slate
const COL_STAIR = "#334155";

export default function MezzanineTopView({ result }) {
  const g = result?.geometry;
  if (!g) return <div className="mono text-xs text-ink-muted">(no geometry)</div>;

  const L = g.length_mm;
  const W = g.width_mm;

  // Outer canvas with margins for dimension labels & staircase overhang
  const margin = 1500; // mm side/bottom
  const topMargin = 2500; // mm — extra room for the secondary-beam sub-chain above main dim chain
  const minX = -margin;
  const totalW = L + margin * 2;
  const totalH = W + margin + topMargin;

  // Render at fixed display width
  const displayW = 880;
  const scale = displayW / totalW;
  const displayH = totalH * scale;

  const bw_L = g.bay_widths_L_mm && g.bay_widths_L_mm.length ? g.bay_widths_L_mm : [L];
  const bw_W = g.bay_widths_W_mm && g.bay_widths_W_mm.length ? g.bay_widths_W_mm : [W];
  const cumL = bw_L.reduce((acc, v) => { acc.push(acc[acc.length - 1] + v); return acc; }, [0]);
  const cumW = bw_W.reduce((acc, v) => { acc.push(acc[acc.length - 1] + v); return acc; }, [0]);
  const labelsL = bw_L.map((v) => Math.round(v));
  const labelsW = bw_W.map((v) => Math.round(v));

  return (
    <div className="overflow-x-auto bg-white border border-line" data-testid="mz-topview">
      <svg
        viewBox={`${minX} ${-(W + topMargin)} ${totalW} ${totalH}`}
        width={displayW}
        height={displayH}
        style={{ display: "block", margin: "0 auto", maxWidth: "100%" }}
        data-testid="mz-topview-svg"
      >
        <g transform={`scale(1, -1)`}>
          {/* Floor outline */}
          <rect x={0} y={0} width={L} height={W} fill="#fafafa" stroke={COL_PILLAR} strokeWidth={20} />

          {/* Secondary beams */}
          {g.secondary_beams.map((b, i) => (
            <line key={`s${i}`} x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2}
                  stroke={COL_SEC} strokeWidth={18} />
          ))}

          {/* Main beams */}
          {g.main_beams.map((b, i) => (
            <line key={`m${i}`} x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2}
                  stroke={COL_BEAM} strokeWidth={36} strokeLinecap="square" />
          ))}

          {/* Pillars */}
          {g.pillars.map((p, i) => (
            <rect key={`p${i}`} x={p.x} y={p.y} width={p.w} height={p.h}
                  fill={COL_PILLAR} stroke="#0c1d59" strokeWidth={6} />
          ))}

          {/* Railings */}
          {g.railings.map((r, i) => (
            <line key={`r${i}`} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2}
                  stroke={COL_RAIL} strokeWidth={24} strokeLinecap="round" />
          ))}

          {/* Staircases */}
          {g.staircases.map((s, i) => (
            <g key={`st${i}`}>
              <rect x={s.x} y={s.y} width={s.w} height={s.h}
                    fill="#fff" stroke={COL_STAIR} strokeWidth={14} />
              {(() => {
                const horizontal = s.w >= s.h;
                const steps = 10;
                const lines = [];
                for (let k = 1; k < steps; k++) {
                  if (horizontal) {
                    const x = s.x + (s.w * k) / steps;
                    lines.push(
                      <line key={`stl${i}-${k}`} x1={x} y1={s.y} x2={x} y2={s.y + s.h}
                            stroke={COL_STAIR} strokeWidth={6} />
                    );
                  } else {
                    const y = s.y + (s.h * k) / steps;
                    lines.push(
                      <line key={`stl${i}-${k}`} x1={s.x} y1={y} x2={s.x + s.w} y2={y}
                            stroke={COL_STAIR} strokeWidth={6} />
                    );
                  }
                }
                return lines;
              })()}
            </g>
          ))}
        </g>

        {/* Dim labels (not flipped) */}
        <DimChainHorizontal y={-(W + 600)} stops={cumL} labels={labelsL} totalLabel={`${L} mm`} />
        <DimChainVertical x={-700} stops={cumW.map((v) => -v)} labels={labelsW} totalLabel={`${W} mm`} />

        {/* Secondary-beam dim sub-chain */}
        {(() => {
          const target = Number(g.sec_target_mm) || 0;
          if (!target) return null;
          const isMainL = (g.main_beam_axis || "L") === "L";
          if (isMainL) {
            const xs = Array.from(new Set(g.secondary_beams.map((b) => Math.round(b.x1))))
              .sort((a, b) => a - b);
            const stops = [0, ...xs, L];
            return <SecDimSubChainHorizontal y={-(W + 2200)} stops={stops} target={target} />;
          }
          const ys = Array.from(new Set(g.secondary_beams.map((b) => Math.round(b.y1))))
            .sort((a, b) => a - b);
          const stops = [0, ...ys, W].map((v) => -v);
          return <SecDimSubChainVertical x={-1300} stops={stops} target={target} />;
        })()}

        {g.staircases.map((s, i) => (
          <text key={`stt${i}`} x={s.x + s.w / 2} y={-(s.y + s.h / 2)}
                textAnchor="middle" dominantBaseline="middle"
                fontFamily="monospace" fontSize={120} fill={COL_STAIR}>
            {s.label}
          </text>
        ))}
      </svg>

      <div className="px-4 py-2 border-t border-line flex flex-wrap gap-4 justify-center text-[0.7rem] text-ink-muted mono bg-surfaceAlt">
        <Legend swatch={COL_PILLAR} label="Pillar" shape="square" />
        <Legend swatch={COL_BEAM} label="Main beam" shape="line" thick />
        <Legend swatch={COL_SEC} label="Secondary beam" shape="line" />
        <Legend swatch={COL_RAIL} label="Railing" shape="line" />
        <Legend swatch={COL_STAIR} label="Staircase" shape="square" />
      </div>
    </div>
  );
}

function Legend({ swatch, label, shape, thick }) {
  return (
    <span className="flex items-center gap-1.5">
      {shape === "square" ? (
        <span className="inline-block w-3 h-3" style={{ backgroundColor: swatch }} />
      ) : (
        <span className="inline-block w-5" style={{ borderTop: `${thick ? 4 : 2}px solid ${swatch}`, height: 0 }} />
      )}
      {label}
    </span>
  );
}

function DimChainHorizontal({ y, stops, labels, totalLabel }) {
  const tickH = 200;
  return (
    <g fontFamily="monospace" fontSize={140} fill={COL_DIM} stroke={COL_DIM} strokeWidth={6}>
      <line x1={stops[0]} y1={y} x2={stops[stops.length - 1]} y2={y} />
      {stops.map((s, i) => (
        <line key={`t${i}`} x1={s} y1={y - tickH} x2={s} y2={y + tickH} />
      ))}
      {labels.map((lab, i) => {
        const xc = (stops[i] + stops[i + 1]) / 2;
        return (
          <text key={`l${i}`} x={xc} y={y - 60} textAnchor="middle" stroke="none">
            {lab}
          </text>
        );
      })}
      <text x={(stops[0] + stops[stops.length - 1]) / 2} y={y - 700}
            textAnchor="middle" stroke="none" fontWeight="700">
        {totalLabel}
      </text>
    </g>
  );
}

function DimChainVertical({ x, stops, labels, totalLabel }) {
  const tickW = 200;
  return (
    <g fontFamily="monospace" fontSize={140} fill={COL_DIM} stroke={COL_DIM} strokeWidth={6}>
      <line x1={x} y1={stops[0]} x2={x} y2={stops[stops.length - 1]} />
      {stops.map((s, i) => (
        <line key={`tv${i}`} x1={x - tickW} y1={s} x2={x + tickW} y2={s} />
      ))}
      {labels.map((lab, i) => {
        const yc = (stops[i] + stops[i + 1]) / 2;
        return (
          <text key={`lv${i}`} x={x - 100} y={yc}
                textAnchor="end" dominantBaseline="middle" stroke="none">
            {lab}
          </text>
        );
      })}
      <text x={x - 800} y={(stops[0] + stops[stops.length - 1]) / 2}
            textAnchor="middle" dominantBaseline="middle" stroke="none" fontWeight="700"
            transform={`rotate(-90 ${x - 800} ${(stops[0] + stops[stops.length - 1]) / 2})`}>
        {totalLabel}
      </text>
    </g>
  );
}

function SecDimSubChainHorizontal({ y, stops, target }) {
  if (stops.length < 2) return null;
  const FONT = 220;
  const TICK = 80;
  return (
    <g>
      {stops.slice(0, -1).map((s, i) => {
        const e = stops[i + 1];
        const gap = Math.round(e - s);
        const isLast = i === stops.length - 2;
        const off = Math.abs(gap - target) > 1;
        const color = isLast && off ? "#dc2626" : "#FF6B1A";
        const strokeColor = isLast && off ? "#dc2626" : "#0f172a";
        return (
          <g key={i}>
            <line x1={s} y1={y} x2={e} y2={y}
                  stroke={strokeColor} strokeWidth={isLast && off ? 22 : 14} />
            <line x1={s} y1={y - TICK} x2={s} y2={y + TICK} stroke={strokeColor} strokeWidth={12} />
            <line x1={e} y1={y - TICK} x2={e} y2={y + TICK} stroke={strokeColor} strokeWidth={12} />
            <text x={(s + e) / 2} y={y - 100}
                  textAnchor="middle" fontFamily="monospace" fontWeight="700"
                  fontSize={FONT} fill={color}>{gap}</text>
          </g>
        );
      })}
    </g>
  );
}

function SecDimSubChainVertical({ x, stops, target }) {
  const ordered = [...stops].sort((a, b) => b - a);
  if (ordered.length < 2) return null;
  const FONT = 220;
  const TICK = 80;
  return (
    <g>
      {ordered.slice(0, -1).map((s, i) => {
        const e = ordered[i + 1];
        const gap = Math.round(Math.abs(e - s));
        const isLast = i === ordered.length - 2;
        const off = Math.abs(gap - target) > 1;
        const color = isLast && off ? "#dc2626" : "#FF6B1A";
        const strokeColor = isLast && off ? "#dc2626" : "#0f172a";
        return (
          <g key={i}>
            <line x1={x} y1={s} x2={x} y2={e}
                  stroke={strokeColor} strokeWidth={isLast && off ? 22 : 14} />
            <line x1={x - TICK} y1={s} x2={x + TICK} y2={s} stroke={strokeColor} strokeWidth={12} />
            <line x1={x - TICK} y1={e} x2={x + TICK} y2={e} stroke={strokeColor} strokeWidth={12} />
            <text x={x - 200} y={(s + e) / 2 + FONT / 3}
                  textAnchor="end" fontFamily="monospace" fontWeight="700"
                  fontSize={FONT} fill={color}>{gap}</text>
          </g>
        );
      })}
    </g>
  );
}
